/* eslint-disable camelcase */
import { useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, Typography, Autocomplete, Divider } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, useQuery, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"
import DetalleArticulosGrid from "./components/DetalleArticulosGrid"
import { useQueryClient } from "@tanstack/react-query"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1300px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const CrearSolicitudIngEgr = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [formData, setFormData] = useState({
    tsolInEg: "",
    solorigen: "",
    tmotcodigo: "",
    sgadescri: "",
    clicodigo: "",
    procodigo: "",
    agencodigosol: "",
    agencodigoenv: "",
    condescrienv: "",
    locsolicita: "",
    locrecibe: "",
    bodegreso: "",
    bodingreso: "",
    detalle: [],
  })

  const [isFetchingMultimedia, setIsFetchingMultimedia] = useState(false)

  // ==========================================================================
  // Convertimos el código a MAYÚSCULAS para evitar fallos de evaluación
  // ==========================================================================
  const t = String(formData.tsolInEg || "").toUpperCase()

  // CASO 1: Visibilidad dinámica dependiendo del tipo de solicitud
  const showDocBase = t === "SOLINGDPRE"
  const showCliente = ["SOLINGGSGA", "SOLEGRGSGA", "SOLEGRPRES", "SOLINGDPRE"].includes(t)
  const showProveedor = ["SOLINGGSGA", "SOLEGRGSGA"].includes(t)
  const showAgencias = t === "SOLEGRGSGA"
  const showLocSolicita = ["SOLEGRLOC", "SOLEGRPRES", "SOLEGRBOD", "SOLINGAJU", "SOLEGRAJU", "SOLINGDPRE"].includes(t)
  const showLocRecibe = t === "SOLEGRLOC"
  const showBodEgreso = ["SOLEGRPRES", "SOLEGRBOD", "SOLEGRAJU"].includes(t)
  const showBodIngreso = ["SOLEGRBOD", "SOLINGAJU", "SOLINGDPRE"].includes(t)

  const { data: catData, isLoading: loadCatLog } = useQuery({
    queryKey: ["catalogosLogisticos", formData.clicodigo],
    queryFn: async () => {
      try {
        const payload = formData.clicodigo ? { clicodigo: formData.clicodigo } : {}
        const response = await api.post("/CrearSolicitudIngEgr/getCatalogosLogisticos", payload)

        let catalogos = {}
        const res = response?.data?.data || response?.data || response || {}

        if (res.localidades) {
          catalogos = res
        } else if (res.data && res.data.localidades) {
          catalogos = res.data
        } else if (response?.data?.data?.data?.localidades) {
          catalogos = response.data.data.data
        }

        return catalogos
      } catch (error) {
        return {}
      }
    },
  })

  const extractArray = (rawList) =>
    Array.isArray(rawList) ? rawList : Array.isArray(rawList?.data) ? rawList.data : []

  const listaTiposSolicitud = extractArray(catData?.tipos_solicitud)
  const listaLocalidades = extractArray(catData?.localidades)
  const listaBodegas = extractArray(catData?.bodegas)
  const listaAgencias = extractArray(catData?.agencias)
  const listaClientes = extractArray(catData?.clientes)
  const listaProveedores = extractArray(catData?.proveedores)
  const listaMotivos = extractArray(catData?.motivos)

  // ==========================================================================
  // OBTENER SOLICITUDES DE PRÉSTAMO PREVIAS PARA DEVOLUCIÓN (CASO 1)
  // ==========================================================================
  const { data: rawSolOrigen, isLoading: loadSolOrigen } = useQuery({
    queryKey: ["listaSolicitudesOrigen"],
    queryFn: async () => {
      try {
        const response = await api.post("/CrearSolicitudIngEgr/getSolicitudesOrigen", { tsolInEg: "SOLEGRPRES" })
        return response?.data?.data || response?.data || []
      } catch (error) {
        return []
      }
    },
    enabled: showDocBase,
  })

  const listaSolOrigen = extractArray(rawSolOrigen).map((item) => ({
    id: item.solnumero,
    label: `${item.solnumero} - ${item.sgadescri || "Préstamo"}`,
    clicodigo: item.clicodigo,
    locsolicita: item.locsolicita,
    bodegreso: item.bodegreso,
  }))

  const { mutateAsync: SaveSolicitud, isPending: isSaving } = useMutation({
    queryKey: ["isCreatingSolicitud"],
    fn: async (data) => {
      const response = await api.post("/CrearSolicitudIngEgr/createSolicitud", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      // ESTA LÍNEA OBLIGA A RECARGAR LA GRILLA PRINCIPAL
      await queryClient.invalidateQueries()
      navigate(-1)
    },
  })

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value.toUpperCase() }))
  }

  const handleTipoSolicitudChange = (event, newValue) => {
    const nuevoTipo = newValue ? String(newValue.id) : ""
    setFormData((prev) => ({
      ...prev,
      tsolInEg: nuevoTipo,
      solorigen: "",
      clicodigo: "",
      procodigo: "",
      agencodigosol: "",
      agencodigoenv: "",
      condescrienv: "",
      locsolicita: "",
      locrecibe: "",
      bodegreso: "",
      bodingreso: "",
      detalle: [],
    }))
  }

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // 1. Validaciones Generales
    if (!formData.tsolInEg) return showWarning("Debe seleccionar el Tipo de Solicitud.")
    if (!formData.tmotcodigo) return showWarning("Debe seleccionar un Motivo.")
    if (!formData.sgadescri.trim()) return showWarning("Debe ingresar una descripción u observación.")

    // 2. Validaciones Condicionales (Si está visible, DEBE estar lleno)
    if (showDocBase && !formData.solorigen) return showWarning("Debe seleccionar el Documento Base a devolver.")
    if (showCliente && !formData.clicodigo) return showWarning("Debe seleccionar un Cliente.")
    if (showProveedor && !formData.procodigo) return showWarning("Debe seleccionar un Proveedor.")
    if (showAgencias && (!formData.agencodigosol || !formData.agencodigoenv))
      return showWarning("Debe seleccionar las agencias que solicitan y reciben.")
    if (showLocSolicita && !formData.locsolicita) return showWarning("Debe seleccionar la Localidad que Solicita.")
    if (showLocRecibe && !formData.locrecibe) return showWarning("Debe seleccionar la Localidad a la que solicitan.")
    if (showBodEgreso && !formData.bodegreso) return showWarning("Debe seleccionar la Bodega de Egreso.")
    if (showBodIngreso && !formData.bodingreso) return showWarning("Debe seleccionar la Bodega de Ingreso.")

    // 3. Validaciones de Transferencias (Evitar mism Origen/Destino)
    if (t === "SOLEGRBOD" && formData.bodegreso === formData.bodingreso) {
      return showWarning("La bodega de egreso y la bodega de ingreso no pueden ser la misma.")
    }
    if (t === "SOLEGRLOC" && formData.locsolicita === formData.locrecibe) {
      return showWarning("La localidad que solicita y la localidad a la que solicitan no pueden ser la misma.")
    }

    if (formData.detalle.length === 0) return showWarning("Debe agregar al menos un artículo al detalle.")

    await SaveSolicitud(formData)
  }

  const guardarAction = selectedMenuInfo?.data?.barraAcciones?.find((a) => a?.acccaption === "GRABAR")
  const toolbarActions = guardarAction
    ? [
        {
          label: guardarAction.acccaption,
          key: guardarAction.acccaption,
          icon: getIconComponent(guardarAction.accnameicono, guardarAction.acctipoico),
        },
      ]
    : [{ label: "Guardar", key: "GUARDAR", icon: <Save /> }]

  // Bloqueamos la pantalla entera si está cargando catálogos, grabando, o consultando multimedia de los artículos
  const loadingGlobal = isSaving || loadCatLog || loadSolOrigen || isFetchingMultimedia

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={loadingGlobal}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Crear Nueva Solicitud de Inventario</b>
        </div>

        <CustomBackdrop isLoading={loadingGlobal} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos de Cabecera
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={listaTiposSolicitud}
                  getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                  value={
                    listaTiposSolicitud.find((c) => String(c.id) === String(formData.tsolInEg)) ||
                    (formData.tsolInEg ? { id: formData.tsolInEg, label: "Cargando..." } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={handleTipoSolicitudChange}
                  renderInput={(params) => (
                    <TextField {...params} label="Tipo de Solicitud *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              {/* CAMPO DE DOCUMENTO BASE */}
              {showDocBase && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaSolOrigen}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaSolOrigen.find((c) => String(c.id) === String(formData.solorigen)) ||
                      (formData.solorigen ? { id: formData.solorigen, label: formData.solorigen } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={async (e, newVal) => {
                      const docBase = newVal ? String(newVal.id) : ""

                      if (docBase) {
                        const cli = newVal.clicodigo ? String(newVal.clicodigo).trim() : ""
                        const loc = newVal.locsolicita ? String(newVal.locsolicita).trim() : ""
                        const bod = newVal.bodegreso ? String(newVal.bodegreso).trim() : ""

                        // Congelamos la cabecera
                        setFormData((prev) => ({
                          ...prev,
                          solorigen: docBase,
                          clicodigo: cli || prev.clicodigo,
                          locsolicita: loc || prev.locsolicita,
                          bodingreso: bod || prev.bodingreso,
                        }))

                        setIsFetchingMultimedia(true) // Encendemos el loader

                        try {
                          const payload = { solnumero: docBase }
                          const response = await api.post("/IngEgrporSolicitud/getDetalleSolicitudInv", payload)

                          let coreData = null
                          const r1 = response?.data
                          if (r1?.data?.data?.solnumero) coreData = r1.data.data
                          else if (r1?.data?.solnumero) coreData = r1.data
                          else if (r1?.solnumero) coreData = r1

                          if (coreData && coreData.detalle) {
                            const itemsFiltrados = coreData.detalle.filter(
                              (item) => parseFloat(item.sgacanegr || 0) > 0,
                            )

                            // Hacemos el fetch de la multimedia para cada fila asíncronamente
                            const detalleConMultimedia = await Promise.all(
                              itemsFiltrados.map(async (item) => {
                                const cantEgresada = parseFloat(item.sgacanegr || 0)
                                const cantIngresada = parseFloat(item.sgacanrec || 0)
                                const cantPendiente = cantEgresada - cantIngresada

                                let imagenes = []
                                let documentos_pdf = []
                                let stock = 0

                                try {
                                  const payloadMulti = {
                                    invcodigo: item.invcodigo,
                                    artcodigo: item.artcodigo,
                                    loccodigo: loc || "",
                                    bodcodigo: bod || "",
                                  }
                                  const mRes = await api.post(
                                    "/CrearSolicitudIngEgr/getMultimediaYStockArticulo",
                                    payloadMulti,
                                  )

                                  // EXTRACCIÓN ULTRA ROBUSTA (Idéntica a la del Grid)
                                  let details = { stock: 0, imagenes: [], documentos_pdf: [] }
                                  const multiRoot = mRes?.data
                                  if (
                                    multiRoot?.data?.data?.imagenes ||
                                    multiRoot?.data?.data?.documentos_pdf ||
                                    multiRoot?.data?.data?.stock !== undefined
                                  ) {
                                    details = multiRoot.data.data
                                  } else if (
                                    multiRoot?.data?.imagenes ||
                                    multiRoot?.data?.documentos_pdf ||
                                    multiRoot?.data?.stock !== undefined
                                  ) {
                                    details = multiRoot.data
                                  } else if (
                                    multiRoot?.imagenes ||
                                    multiRoot?.documentos_pdf ||
                                    multiRoot?.stock !== undefined
                                  ) {
                                    details = multiRoot
                                  }

                                  imagenes = Array.isArray(details.imagenes) ? details.imagenes : []
                                  documentos_pdf = Array.isArray(details.documentos_pdf) ? details.documentos_pdf : []
                                  stock = parseFloat(details.stock) || 0
                                } catch (err) {
                                  console.error("Error trayendo multimedia para", item.artcodigo)
                                }

                                return {
                                  ...item,
                                  sgacanegr: cantEgresada,
                                  sgacanrec: cantIngresada,
                                  cant_egresada: cantEgresada,
                                  cant_ingresada: cantIngresada,
                                  cant_pendiente: cantPendiente,
                                  sgacansol: cantPendiente > 0 ? cantPendiente : 0,
                                  stock, // <-- LA VARIABLE QUE FALTABA
                                  imagenes,
                                  documentos_pdf,
                                  isDocBase: true,
                                  locked: true,
                                }
                              }),
                            )

                            setFormData((prev) => ({
                              ...prev,
                              detalle: detalleConMultimedia,
                            }))
                          }
                        } catch (error) {
                          showWarning("Error al obtener el detalle del documento base.")
                        } finally {
                          setIsFetchingMultimedia(false)
                        }
                      } else {
                        setFormData((prev) => ({
                          ...prev,
                          solorigen: "",
                          clicodigo: "",
                          locsolicita: "",
                          bodingreso: "",
                          detalle: [],
                        }))
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Documento Base (Solicitud Origen) *"
                        placeholder="Busque la solicitud a devolver..."
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* Si showDocBase está oculto, el combo de Motivos toma su lugar */}
              <Grid item xs={12} sm={showDocBase ? 12 : 6}>
                <Autocomplete
                  options={listaMotivos}
                  getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                  value={
                    listaMotivos.find((c) => String(c.id) === String(formData.tmotcodigo)) ||
                    (formData.tmotcodigo ? { id: formData.tmotcodigo, label: "Cargando..." } : null)
                  }
                  isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                  onChange={(e, newVal) =>
                    setFormData((prev) => ({ ...prev, tmotcodigo: newVal ? String(newVal.id) : "" }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Motivo *" InputLabelProps={{ shrink: true }} />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descripción / Observación *"
                  value={formData.sgadescri}
                  onChange={(e) => handleInputChange("sgadescri", e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              {showCliente && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    disabled={showDocBase && !!formData.solorigen}
                    options={listaClientes}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaClientes.find((c) => String(c.id) === String(formData.clicodigo)) ||
                      (formData.clicodigo ? { id: formData.clicodigo, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) => {
                      setFormData((prev) => ({
                        ...prev,
                        clicodigo: newVal ? String(newVal.id) : "",
                        agencodigosol: "",
                        agencodigoenv: "",
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Cliente *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showProveedor && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={listaProveedores}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaProveedores.find((c) => String(c.id) === String(formData.procodigo)) ||
                      (formData.procodigo ? { id: formData.procodigo, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, procodigo: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Proveedor *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showAgencias && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      disabled={!formData.clicodigo}
                      options={listaAgencias}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaAgencias.find((c) => String(c.id) === String(formData.agencodigosol)) ||
                        (formData.agencodigosol ? { id: formData.agencodigosol, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, agencodigosol: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Agencia que Solicita *" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Autocomplete
                      disabled={!formData.clicodigo}
                      options={listaAgencias}
                      getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                      value={
                        listaAgencias.find((c) => String(c.id) === String(formData.agencodigoenv)) ||
                        (formData.agencodigoenv ? { id: formData.agencodigoenv, label: "Cargando..." } : null)
                      }
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                      onChange={(e, newVal) =>
                        setFormData((prev) => ({ ...prev, agencodigoenv: newVal ? String(newVal.id) : "" }))
                      }
                      renderInput={(params) => (
                        <TextField {...params} label="Agencia de Envío *" InputLabelProps={{ shrink: true }} />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12}>
                    <TextField
                      fullWidth
                      label="Contacto de Agencia de Envío"
                      value={formData.condescrienv}
                      onChange={(e) => handleInputChange("condescrienv", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: 60 }}
                    />
                  </Grid>
                </>
              )}

              {showLocSolicita && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    // Desactivado en flujos documentales de retorno
                    disabled={showDocBase && !!formData.solorigen}
                    options={listaLocalidades}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaLocalidades.find((c) => String(c.id) === String(formData.locsolicita)) ||
                      (formData.locsolicita ? { id: formData.locsolicita, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) => {
                      setFormData((prev) => ({
                        ...prev,
                        locsolicita: newVal ? String(newVal.id) : "",
                        bodegreso: "",
                        bodingreso: "",
                      }))
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Localidad que Solicita *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showLocRecibe && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    options={listaLocalidades}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaLocalidades.find((c) => String(c.id) === String(formData.locrecibe)) ||
                      (formData.locrecibe ? { id: formData.locrecibe, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, locrecibe: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Localidad a la que solicitan *"
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  />
                </Grid>
              )}

              {showBodEgreso && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    disabled={!formData.locsolicita}
                    options={listaBodegas.filter((b) => b.loccodigo === formData.locsolicita)}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaBodegas.find((c) => String(c.id) === String(formData.bodegreso)) ||
                      (formData.bodegreso ? { id: formData.bodegreso, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, bodegreso: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Bodega de Egreso *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}

              {showBodIngreso && (
                <Grid item xs={12} sm={4}>
                  <Autocomplete
                    disabled={showDocBase && !!formData.solorigen}
                    options={listaBodegas.filter((b) => b.loccodigo === formData.locsolicita)}
                    getOptionLabel={(option) => option?.label || String(option?.id) || ""}
                    value={
                      listaBodegas.find((c) => String(c.id) === String(formData.bodingreso)) ||
                      (formData.bodingreso ? { id: formData.bodingreso, label: "Cargando..." } : null)
                    }
                    isOptionEqualToValue={(option, value) => String(option.id) === String(value?.id)}
                    onChange={(e, newVal) =>
                      setFormData((prev) => ({ ...prev, bodingreso: newVal ? String(newVal.id) : "" }))
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Bodega de Ingreso *" InputLabelProps={{ shrink: true }} />
                    )}
                  />
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h6" color="primary" gutterBottom>
              Detalle de Artículos
            </Typography>

            {formData.tsolInEg ? (
              <DetalleArticulosGrid
                detalle={formData.detalle}
                setFormData={setFormData}
                tsolInEg={formData.tsolInEg}
                clicodigo={formData.clicodigo}
                loccodigo={formData.locsolicita || formData.locrecibe}
                bodcodigo={formData.bodegreso || formData.bodingreso}
                isDocBase={showDocBase && !!formData.solorigen}
              />
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic", mt: 2 }}>
                Seleccione un Tipo de Solicitud para habilitar la carga de artículos.
              </Typography>
            )}
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default CrearSolicitudIngEgr
